import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Typography,
  Chip,
  Divider,
  Container,
  Paper,
  Grid,
  Stack
} from "@mui/material";
import moment from "moment";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import PrintIcon from '@mui/icons-material/Print';
import SyncIcon from '@mui/icons-material/Sync';
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import ConfirmStartDialog from "../sections/retail-price-tags/ConfirmStartDialog";
import ScanDialog from "../sections/retail-price-tags/ScanDialog";
import ConfirmPDFCreationDialog from "../sections/retail-price-tags/ConfirmPDFCreationDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;
const ROW_HEIGHT = 70;

const defaultFilter = {
  date: null,
};

const STATUS = {
  downloading: "Retrieving data from Cin7",
  pending: "Ready to start",
  open: "In Progress",
  generated: "Closed",
  failed: "Sync failed",
};

const STATUS_COLOR = {
  downloading: "info",
  pending: "warning",
  open: "warning",
  generated: "success",
  failed: "error"
};

export default function RetailPriceTags() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [filter, setFilter] = useState(defaultFilter);
  const [showScanner, setShowScanner] = useState(false);
  const [pendingPdfId, setPendingPdfId] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);

  const [currentQueueId, setCurrentQueueId] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false)

  const goToDetailView = (id) => {
    setCurrentQueueId(id);
    setShowScanner(true);
  };

  let active = useRef(false);

  const loadQueues = async (reset) => {
    if (isLoading) {
      return
    }
    setIsLoading(true);
    try {
      let query = {
        limit: PAGE_SIZE,
        offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
      };

      if (filter.date) {
        query.dateFrom = moment(filter.date).startOf("day").toISOString();
        query.dateTo = moment(filter.date).endOf("day").toISOString();
      }

      const params = new URLSearchParams(query);
      const response = await fetchWithInterceptor(`/api/product-labels/print-files?${params}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });

      if (response.ok) {
        if (!active.current) return;
        const data = await response.json();
        if (reset) {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }
        setProducts(data.logs);
        setRowCount(data.total);
      } else {
        console.log(response);
        appContext.showMessage("There was an error getting the print files", "error");
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Unexpected error loading print files", "error");
    }
    setIsLoading(false);
  }

  const handleDownloadPdf = async (pdfId) => {
    try {
      const response = await fetchWithInterceptor(
        `/api/product-labels/download-pdf/${pdfId}`,
        {
          method: "GET",
          headers: {
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error opening PDF:', err);
      appContext.showMessage('Error opening PDF', 'error');
    }
  };

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadQueues(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadQueues(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadQueues(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showConfirmStart = () => setConfirmStart(true)
  const hideConfirmStart = () => {
    setConfirmStart(false)
    loadQueues(true)
  }

  const columns = [
    {
      field: "id",
      headerName: "Ref",
      width: 80,
      sortable: false,
      valueGetter: (value) => `#${value}`,
    },
    {
      field: "createdAt",
      headerName: "Created at",
      width: 200,
      sortable: false,
      valueGetter: (value) =>
        value ? format(new Date(value), "yyyy-MM-dd hh:mm a") : "",
    },
    {
      field: "status",
      headerName: "Status",
      width: 230,
      sortable: false,
      renderCell: (params) => 
        {
        const value = params.row.status;
        return (
          <Chip
            label={STATUS[value] || value}
            color={STATUS_COLOR[value] || "default"}
            size="small"
          />
        );
      },
    },
     {
      field: "items",
      headerName: "Scanned",
      width: 100,
      sortable: false,
      valueGetter: (value, row) => row?._count?.items ?? 0,
    },
    {
      field: "scan",
      headerName: "Actions",
      width: 450,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { id, status } = params.row;
        return (
          <Stack flexDirection="row" alignItems="center" sx={{ height: "100%" }}>
            {(status === "pending" || status === "open") && (
              <Button onClick={() => goToDetailView(id)} size="small" sx={{ mr: 2 }} startIcon={<DocumentScannerIcon />}>
                {`${status === "pending" ? "Start" : "Continue"} scanning`}
              </Button>
            )}
            {status === "open" && (
              <Button
                onClick={() => {
                  setPendingPdfId(id);
                  setShowPrintDialog(true);
                }}
                size="small"
                sx={{ mr: 2 }}
                startIcon={<PrintIcon />}
              >
                Generate Labels
              </Button>
            )}
            {status === "generated" && (
              <Button
                onClick={() => handleDownloadPdf(id)}
                size="small"
                startIcon={<PrintIcon />}
              >
                Open&nbsp;PDF
              </Button>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Scan a bottle's barcode to instantly generate and print shelf-ready retail labels.
      </Typography>

      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Retail Price Tags</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Created at"
                  value={filter.date}
                  onChange={(date) => setFilter({ ...filter, date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </div>
          <Divider />
          <Grid
            container
            spacing={2}
            style={{ padding: 16 }}
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "center", sm: "flex-end" }}
          >
            <Grid size={{ xs:12, sm: "auto" }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ minWidth: { sm: "160px" } }}
                onClick={showConfirmStart}
                startIcon={<SyncIcon />}
              >
                Start process
              </Button>
            </Grid>
          </Grid>

          <DataGrid
            rows={products}
            columns={columns}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            disableRowSelectionOnClick
            disableColumnFilter
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={rowCount}
            rowHeight={ROW_HEIGHT}
            sx={{
              height: ROW_HEIGHT * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
                whiteSpace: "wrap",
                lineHeight: 1,
              },
              "& .MuiDataGrid-cell--withRenderer": {
                display: "block",
                textOverflow: "unset",
              },
              "& .MuiDataGrid-cell--withRenderer span": {
                overflow: "hidden",
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                display: "block",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
                justifyContent: "center",
              },
            }}
          />
        </Paper>
      </Container>
      {/* Escaner */}
      <ScanDialog
        open={showScanner}
        onClose={() => {
          setShowScanner(false);
          loadQueues(true);
        }}
        currentQueueId={currentQueueId}
      />
      <ConfirmStartDialog 
        open={confirmStart}
        onClose={hideConfirmStart}
      />
      {/* confirmación para Generate PDF */}
      <ConfirmPDFCreationDialog
        open={showPrintDialog}
        pendingPdfId={pendingPdfId}
        onClose={() => {
          setShowPrintDialog(false);
          setPendingPdfId(null);
        }}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        refresh={() => loadQueues(true)}
      />
    </>
  );
}